import { Volume2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { EvidenceKind } from '../../engine/learningEvidence'
import { audioManager, type PlaybackResult } from '../../services/AudioManager'
import type { Lesson, Word } from '../../types/content'
import type { AttemptDetails, LearningMode, LearningSkill } from '../../types/progress'

export interface ActivityProps {
  lesson: Lesson
  mistakes: number
  disabled: boolean
  mode: LearningMode
  answer(correct: boolean, evidence?: EvidenceKind, details?: AttemptDetails): void
  observe(correct: boolean, evidence: EvidenceKind, details: AttemptDetails): void
}

export function useActivity(props: ActivityProps, word?: Word) {
  const [heard, setHeard] = useState(false)
  const [busy, setBusy] = useState(false)
  const [missing, setMissing] = useState(false)
  const [assistance, setAssistance] = useState<0 | 1 | 2 | 3>(0)
  const assistanceRef = useRef<0 | 1 | 2 | 3>(0)
  const alive = useRef(true)
  const request = useRef(0)
  const started = useRef(Date.now())
  useEffect(() => { alive.current = true; return () => { alive.current = false; audioManager.stop() } }, [])
  const help = (level: 1 | 2 | 3) => {
    assistanceRef.current = Math.max(assistanceRef.current, level) as 1 | 2 | 3
    setAssistance(assistanceRef.current)
  }
  const listen = async (play: () => Promise<PlaybackResult>, question = false) => {
    const id = ++request.current
    setBusy(true); setMissing(false)
    const result = await play()
    if (!alive.current || id !== request.current) return result
    setBusy(false)
    if (question && result === 'ended') setHeard(true)
    if (result === 'unavailable') setMissing(true)
    return result
  }
  const details = (skill: LearningSkill, extra: Partial<AttemptDetails> = {}): AttemptDetails => ({
    skill, assistanceLevel: assistanceRef.current, attemptNumber: props.mistakes + 1,
    wordId: word?.id, responseMs: Date.now() - started.current, mode: props.mode, ...extra,
  })
  const submit = (correct: boolean, skill: LearningSkill, evidence: EvidenceKind = 'checked', extra: Partial<AttemptDetails> = {}) => {
    if (!props.disabled && !busy && !missing) props.answer(correct, evidence, details(skill, extra))
  }
  return { heard, busy, missing, assistance, help, listen, details, submit, blocked: props.disabled || busy || missing }
}

type Task = ReturnType<typeof useActivity>
export function Listen({ task, play, label = '听一听', disabled = false }: { task: Task; play(): Promise<PlaybackResult>; label?: string; disabled?: boolean }) {
  return <button disabled={disabled} onClick={() => { if (task.heard) task.help(1); void task.listen(play, true) }} className="inline-flex min-h-16 items-center justify-center gap-3 rounded-full bg-[#2b503e] px-7 text-lg font-extrabold text-white shadow-button disabled:opacity-50"><Volume2 size={26} />{task.busy ? '正在播放 · 再听' : label}</button>
}

export function Help({ task, play, word, demonstration, disabled }: { task: Task; play(): Promise<PlaybackResult>; word?: Word; demonstration: string; disabled: boolean }) {
  const [open, setOpen] = useState(false)
  const [demo, setDemo] = useState(false)
  return <div className="w-full max-w-md text-center">
    {task.missing && <p role="alert" className="mb-3 rounded-2xl bg-amber-50 p-4 text-sm text-garden-800">暂时听不到。再点一次喇叭试试，也可以回花园；这不算答错。</p>}
    <button className="quiet-button !text-sm" disabled={disabled} aria-expanded={open} onClick={() => setOpen(!open)}>帮我一下</button>
    {open && <div className="mt-3 flex flex-wrap justify-center gap-2">
      <button className="quiet-button border border-garden-200 !text-sm" disabled={disabled} onClick={() => { task.help(1); void task.listen(play, true) }}>再听一次</button>
      {word && <button className="quiet-button border border-garden-200 !text-sm" disabled={disabled} onClick={() => { task.help(2); void task.listen(() => audioManager.playPhonemes(word.phonemeIds)) }}>分开听</button>}
      <button className="quiet-button border border-garden-200 !text-sm" disabled={disabled} onClick={() => { task.help(3); setDemo(true); void task.listen(word ? () => audioManager.playPhonemes(word.phonemeIds) : play) }}>看示范</button>
    </div>}
    {demo && <div className="mt-3 rounded-2xl bg-[#fff5d5] p-4" role="status"><p className="font-bold">{demonstration}</p><button className="quiet-button mt-2 !text-sm" onClick={() => setDemo(false)}>我来试一试</button></div>}
  </div>
}

export function Segments({ word, task, disabled, active = -1 }: { word: Word; task: Task; disabled: boolean; active?: number }) {
  let soundIndex = 0
  return <div className="flex flex-wrap justify-center gap-3">{word.mapping.map((mapping, index) => {
    const playing = active >= soundIndex && active < soundIndex + mapping.phonemeIds.length
    soundIndex += mapping.phonemeIds.length
    return <button key={index} disabled={disabled} className={`sound-token ${playing ? 'is-playing' : ''}`} aria-label={`听 ${mapping.grapheme} 的声音`} onClick={() => { task.help(1); void task.listen(() => audioManager.playPhonemes(mapping.phonemeIds)) }}>{mapping.grapheme.replace('_', '·')}</button>
  })}</div>
}
